// «Действия» — core in-campaign screen. Four sub-tabs (Действия / Диалоги /
// Чаты / Инициативы); only «Действия» is rebuilt so far, the rest are stubs.
// Isolated .gn-* markup; all logic reused from cycle/ActionForm + ActionSection
// so behaviour matches the live screen (free-text move → parse → performCycleAction).

import { useMemo, useState, type FormEvent } from "react";
import { api, type CampaignSetup, type CycleAction } from "../api";
import { phaseInfo } from "../cycle/shared";
import {
  AfIcon,
  actionHooks,
  coActorAvailability,
  roleLeverProfile,
  understandActionDraft,
} from "../cycle/ActionForm";
import { actionContextLabel, latestActionTitle, outcomeLabel } from "../cycle/ActionSection";
import { SituationModal } from "./SituationModal";
import { GnCycleImage } from "./GnCycleImage";
import { GnChatsTab, GnDialoguesTab } from "./RoomsScreen";
import { GnInitiativesTab } from "./InitiativesScreen";

const GEN = "/assets/game-ui/gen";

export type Workspace = "actions" | "dialogues" | "chats" | "initiatives";
type RunAction = (label: string, run: () => Promise<unknown>) => Promise<boolean>;

function resourceMedallion(text: string): string {
  const s = text.toLowerCase();
  if (/убеж|housing|жиль|дом|бункер/.test(s)) return "res_housing";
  if (/провиз|food|еда|прод|паёк|припас/.test(s)) return "res_food";
  if (/энерг|energy|топлив|электр|генерат/.test(s)) return "res_energy";
  if (/связ|signal|коммуник|информ|радио/.test(s)) return "res_signal";
  if (/медик|medic|лекар|санитар|аптеч|лазарет/.test(s)) return "res_medicine";
  return "hook_institution";
}
function hookMedallion(hook: { title: string; tone?: string }): string {
  switch (hook.tone) {
    case "danger":
      return "threat_unrest";
    case "politics":
      return "hook_faction";
    case "knowledge":
      return "nav_chronicle";
    case "resource":
      return resourceMedallion(hook.title);
    default:
      return "hook_faction";
  }
}
function hookClass(tone?: string): string {
  if (tone === "danger") return "d";
  if (tone === "resource") return "r";
  return "f";
}
const OUTCOME_CLASS: Record<string, string> = { SUCCESS: "ok", PARTIAL_SUCCESS: "part", FAILURE: "fail" };
function actionSummary(action: CycleAction): string {
  const pending = action.narrativeStatus !== "READY";
  if (pending) return action.description;
  return (
    action.deferredOutcomeText ||
    action.achievementText ||
    action.intentSummary ||
    action.description
  );
}

export function ActionsScreen({
  campaignId,
  setup,
  action,
  ws,
  setWs,
  onOpenSettings,
}: {
  campaignId: string;
  setup: CampaignSetup;
  action: RunAction;
  ws: Workspace;
  setWs: (w: Workspace) => void;
  onOpenSettings?: () => void;
}) {
  void onOpenSettings;
  const cycle = setup.cycle.current;
  // ws/setWs подняты в GnCampaignShell — чтобы переходы с «Мира» вели в конкретную под-вкладку.
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<"PUBLIC" | "PRIVATE">("PUBLIC");
  const [coActors, setCoActors] = useState<string[]>([]);
  const [situationOpen, setSituationOpen] = useState(false);
  const [detailAction, setDetailAction] = useState<CycleAction | null>(null);

  const currentMember = setup.members.find((m) => m.isCurrentUser);
  const currentCharacter = currentMember
    ? setup.characters.find((c) => c.userId === currentMember.userId)
    : undefined;
  const officeCode = currentCharacter?.officeCode;

  const roleProfile = useMemo(
    () => roleLeverProfile(officeCode, currentCharacter?.publicTitle ?? setup.commandHub?.character.title),
    [officeCode, currentCharacter?.publicTitle, setup.commandHub?.character.title],
  );
  const hooks = useMemo(() => (cycle ? actionHooks(setup, cycle) : []), [setup, cycle]);
  const understanding = useMemo(
    () => understandActionDraft(description, officeCode, setup),
    [description, officeCode, setup],
  );
  const progressByUserId = useMemo(
    () => new Map((cycle?.memberProgress ?? []).map((p) => [p.userId, p])),
    [cycle?.memberProgress],
  );

  if (!cycle) {
    return (
      <div className="gn-body">
        <div className="gn-card gn-empty">Цикл ещё не начался.</div>
      </div>
    );
  }

  const remaining = Math.max(0, cycle.actionLimit - cycle.actionsUsed);
  const enough = description.trim().length >= 20;
  const canSend = cycle.canAct && enough;
  // Блок создания действия виден, только пока действительно можно ходить: не в
  // фазе встреч/инициатив, день не завершён (canAct) и ходы не исчерпаны.
  const canCreateAction = cycle.canAct && remaining > 0;
  const situation = setup.commandHub?.situation;
  const recent = [...cycle.actions]
    .sort((l, r) => r.createdAt.localeCompare(l.createdAt))
    .slice(0, 8);

  const npcActive = (cycle.npcVisits ?? []).filter((v) => v.status === "ACTIVE").length;
  const badges: Record<Workspace, number> = {
    actions: 0,
    dialogues: npcActive,
    chats: cycle.unread?.chatCount ?? 0,
    initiatives: cycle.unread?.initiativeCount ?? 0,
  };
  const TABS: { id: Workspace; label: string }[] = [
    { id: "actions", label: "Действия" },
    { id: "dialogues", label: "Диалоги" },
    { id: "chats", label: "Чаты" },
    { id: "initiatives", label: "Инициативы" },
  ];

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSend) return;
    const inferred = understandActionDraft(description, officeCode, setup);
    const ok = await action(
      coActors.length > 0 ? "Отправляем совместное действие…" : "Действие принято…",
      () =>
        api.performCycleAction(campaignId, {
          actionType: inferred.actionType,
          visibility,
          targetCode: inferred.targetCode || undefined,
          description,
          coActorUserIds: coActors,
        }),
    );
    if (ok) {
      setDescription("");
      setCoActors([]);
    }
  }

  function toggleCoActor(userId: string) {
    setCoActors((cur) => (cur.includes(userId) ? cur.filter((id) => id !== userId) : [...cur, userId]));
  }

  async function toggleDayReady(ready: boolean) {
    await action(ready ? "Завершаем день…" : "Возвращаемся к действиям…", () =>
      api.setDayReadiness(campaignId, ready),
    );
  }

  const otherMembers = setup.members.filter((m) => !m.isCurrentUser);

  return (
    <>
      <div className="gn-sub">
        <div className="gn-sub-pill">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={ws === t.id ? "on" : ""}
              onClick={() => setWs(t.id)}
            >
              {t.label}
              {badges[t.id] > 0 && <i>{badges[t.id]}</i>}
            </button>
          ))}
        </div>
      </div>

      <div className="gn-screen">
        {ws === "actions" ? (
          <form className="gn-body" onSubmit={submit}>
            {situation && (
              <button type="button" className="gn-card gn-situ" onClick={() => setSituationOpen(true)}>
                <span className="gn-cameo">
                  <img src={`${GEN}/med_situation.png`} alt="" />
                </span>
                <div className="m">
                  <span className="gn-eyebrow">Ситуация дня</span>
                  <h2>{situation.title}</h2>
                  <p>{situation.shortText}</p>
                  {(situation.centralProblem || situation.mainObjective) && (
                    <p className="gn-situ-task">
                      <b>Задача:</b> {situation.centralProblem || situation.mainObjective}
                    </p>
                  )}
                  <div className="foot">
                    {cycle.canAct ? (
                      <>
                        Осталось <b>{remaining}</b> из {cycle.actionLimit} ходов
                        <span className="gn-pips">
                          {Array.from({ length: cycle.actionLimit }, (_, i) => (
                            <i key={i} className={i < remaining ? "" : "sp"} />
                          ))}
                        </span>
                      </>
                    ) : (
                      <>{phaseInfo(cycle.phase).label}</>
                    )}
                  </div>
                </div>
              </button>
            )}

            {canCreateAction && (
              <>
            <div className="gn-card gn-role">
              <div className="rhead">
                <span className="gn-cameo">
                  <img src={`${GEN}/role_office.png`} alt="" />
                </span>
                <div className="m">
                  <span className="gn-eyebrow">Ваши рычаги</span>
                  <h2>{roleProfile.title}</h2>
                  <div className="tag">{roleProfile.tagline}</div>
                </div>
              </div>
              <div className="gn-levers">
                {roleProfile.levers.map((lever, i) => (
                  <div className="gn-lev" key={i}>
                    <span className="lic">
                      <AfIcon name={lever.icon} />
                    </span>
                    <div className="lt">
                      <b>{lever.title}</b>
                      <span>{lever.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
              {roleProfile.limits.length > 0 && (
                <div className="gn-locks">
                  {roleProfile.limits.map((limit, i) => (
                    <div className="gn-lev lock" key={i}>
                      <span className="lic">
                        <AfIcon name="lock" />
                      </span>
                      <div className="lt">
                        <b>{limit.title}</b>
                        <span>{limit.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {hooks.length > 0 && (
              <div className="gn-card">
                <span className="gn-eyebrow">Зацепки дня</span>
                <div className="gn-hooks">
                  {hooks.map((hook, i) => (
                    <div className={`gn-hook ${hookClass(hook.tone)}`} key={i}>
                      <span className="hm">
                        <img src={`${GEN}/${hookMedallion(hook)}.png`} alt="" />
                      </span>
                      <b>{hook.title}</b>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {cycle.privateBriefing && (
              <div className="gn-card">
                <span className="gn-eyebrow gn-priv">Служебная сводка дня · только для вас</span>
                <p className="gn-mtext">{cycle.privateBriefing}</p>
                {setup.myPrivateProfile?.briefingSharingGuidance && (
                  <div className="gn-privnote">Можно ли обсуждать: {setup.myPrivateProfile.briefingSharingGuidance}</div>
                )}
              </div>
            )}

            <div className="gn-card">
              <span className="gn-eyebrow">Что вы делаете?</span>
              <div className="gn-ta-wrap">
                <textarea
                  className="gn-ta"
                  maxLength={1200}
                  placeholder="Опишите действие своими словами…"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <span className="gn-counter">{description.length}/1200</span>
              </div>
              <div className="gn-cfoot">
                <span className="gn-hint">
                  <AfIcon name="chat" />
                  Система сама поймёт смысл действия и последствия.
                </span>
                <div className="gn-seg">
                  <button
                    type="button"
                    className={visibility === "PUBLIC" ? "on" : ""}
                    onClick={() => setVisibility("PUBLIC")}
                  >
                    Публично
                  </button>
                  <button
                    type="button"
                    className={visibility === "PRIVATE" ? "on" : ""}
                    onClick={() => setVisibility("PRIVATE")}
                  >
                    Лично
                  </button>
                </div>
              </div>
            </div>

            <div className="gn-card">
              <span className="gn-eyebrow">Игра поняла ход так</span>
              <div className="gn-parse">
                <div className="gn-prow">
                  <span className="gn-pk">
                    <AfIcon name="target" />
                    Цель
                  </span>
                  <span className={`gn-pv${enough ? "" : " dim"}`}>{understanding.goal}</span>
                </div>
                <div className="gn-prow">
                  <span className="gn-pk">
                    <AfIcon name="wrench" />
                    Способ
                  </span>
                  <span className={`gn-pv${enough ? "" : " dim"}`}>{understanding.method}</span>
                </div>
                <div className="gn-prow">
                  <span className="gn-pk">
                    <AfIcon name="spark" />
                    Может дать
                  </span>
                  <span className="gn-pv">{understanding.possibleOutcome}</span>
                </div>
                <div className="gn-prow">
                  <span className="gn-pk">
                    <AfIcon name="users" />
                    Нужен ли союзник
                  </span>
                  <span className={`gn-pv${enough ? "" : " dim"}`}>
                    {enough
                      ? understanding.needAlly
                        ? "Да — сильнее с профильным союзником"
                        : "Нет — можно в одиночку"
                      : "будет определено из описания"}
                  </span>
                </div>
              </div>
            </div>

            {otherMembers.length > 0 && (
              <details className="gn-invite-details">
                <summary className="gn-darkbtn">
                  <AfIcon name="users" />
                  Пригласить игрока в действие
                  <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </summary>
                <div className="gn-coactors">
                  {otherMembers.map((m) => {
                    const avail = coActorAvailability(progressByUserId.get(m.userId));
                    return (
                      <label className={`gn-coactor${avail.available ? "" : " off"}`} key={m.userId}>
                        <input
                          type="checkbox"
                          disabled={!avail.available}
                          checked={coActors.includes(m.userId)}
                          onChange={() => toggleCoActor(m.userId)}
                        />
                        <b>{m.displayName}</b>
                        <span>{avail.text}</span>
                      </label>
                    );
                  })}
                </div>
              </details>
            )}

            <button type="submit" className="gn-send" disabled={!canSend}>
              Отправить действие
            </button>
              </>
            )}

            {cycle.phase === "DAY" && cycle.myDayReady && cycle.actionsUsed < cycle.actionLimit && (
              <button type="button" className="gn-darkbtn" onClick={() => toggleDayReady(false)}>
                Вернуться к действиям
              </button>
            )}
            {cycle.phase === "DAY" && !cycle.myDayReady && cycle.canMarkDayReady && (
              <button type="button" className="gn-darkbtn" onClick={() => toggleDayReady(true)}>
                Завершить день
              </button>
            )}

            <div>
              <span className="gn-eyebrow">Последние действия</span>
              {recent.length === 0 ? (
                <div className="gn-empty">В этом цикле ещё никто не выполнил действие.</div>
              ) : (
                <div className="gn-acts">
                  {recent.map((act) => {
                    const ready = act.narrativeStatus === "READY";
                    return (
                      <button
                        type="button"
                        className="gn-act"
                        key={act.id}
                        onClick={() => setDetailAction(act)}
                      >
                        <span className="gn-amed">
                          <img src={`${GEN}/act_result.png`} alt="" />
                        </span>
                        <div className="gn-atext">
                          <div className="gn-atop">
                            <h3>{latestActionTitle(act)}</h3>
                            {ready && (
                              <span className={`gn-atag ${OUTCOME_CLASS[act.outcome] ?? "part"}`}>
                                {outcomeLabel(act.outcome)}
                              </span>
                            )}
                          </div>
                          <p>{actionSummary(act)}</p>
                          <span className="gn-ameta">{actionContextLabel(act)}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </form>
        ) : ws === "dialogues" ? (
          <GnDialoguesTab campaignId={campaignId} setup={setup} action={action} />
        ) : ws === "chats" ? (
          <GnChatsTab campaignId={campaignId} setup={setup} action={action} />
        ) : (
          <GnInitiativesTab campaignId={campaignId} setup={setup} action={action} />
        )}
      </div>

      {situationOpen && situation && (
        <SituationModal situation={situation} onClose={() => setSituationOpen(false)} />
      )}
      {detailAction && (
        <ActionDetailModal
          campaignId={campaignId}
          action={detailAction}
          onClose={() => setDetailAction(null)}
          onCreateInitiative={() => {
            setDetailAction(null);
            setWs("initiatives");
          }}
        />
      )}
    </>
  );
}

function ActionDetailModal({
  campaignId,
  action,
  onClose,
  onCreateInitiative,
}: {
  campaignId: string;
  action: CycleAction;
  onClose: () => void;
  onCreateInitiative: () => void;
}) {
  const ready = action.narrativeStatus === "READY";
  const g = action.guidance;
  const hasGuidance = ready && !!g && !!(g.whatHappenedText || g.whatChangedText || g.whatNextText);
  const images = ready ? action.images.filter((a) => a.status !== "FAILED") : [];
  return (
    <div className="gn-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="gn-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gn-modal-head">
          <span className="gn-eyebrow">Итог действия</span>
          <button type="button" className="gn-modal-x" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>
        <h2>{latestActionTitle(action)}</h2>
        <div className="gn-act-meta">
          {ready && (
            <span className={`gn-atag ${OUTCOME_CLASS[action.outcome] ?? "part"}`}>{outcomeLabel(action.outcome)}</span>
          )}
          <span className="gn-ameta">{actionContextLabel(action)}</span>
        </div>
        {images.length > 0 && (
          <div className="gn-detail-img">
            {images.map((asset) => (
              <GnCycleImage key={asset.id} campaignId={campaignId} asset={asset} />
            ))}
          </div>
        )}
        {hasGuidance ? (
          <div className="gn-detail">
            {g!.whatHappenedText && (
              <div className="gn-drow">
                <span>Что произошло</span>
                <p>{g!.whatHappenedText}</p>
              </div>
            )}
            {g!.whatChangedText && (
              <div className="gn-drow">
                <span>Что изменилось</span>
                <p>{g!.whatChangedText}</p>
              </div>
            )}
            {g!.whatNextText && (
              <div className="gn-drow">
                <span>Что дальше</span>
                <p>{g!.whatNextText}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="gn-modal-body">
            <p>{actionSummary(action)}</p>
          </div>
        )}
        {ready && action.requiresInitiative && action.isMine && (
          <button type="button" className="gn-cta gn-detail-cta" onClick={onCreateInitiative}>
            Закрепить инициативой
          </button>
        )}
      </div>
    </div>
  );
}
